import React, { useRef, useState } from 'react';
import './PhotoUpload.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface PhotoUploadProps {
  value: File | null;
  previewUrl: string | null;
  onChange: (file: File | null, previewUrl: string | null) => void;
  onUpload?: (file: File) => Promise<string>;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  error?: string;
  maxSizeMB?: number;
  instantUpload?: boolean;
  className?: string;
  label?: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  value,
  previewUrl,
  onChange,
  onUpload,
  disabled = false,
  required = false,
  helperText,
  error,
  maxSizeMB = 5,
  instantUpload = false,
  className = '',
  label = 'Choose File',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Only image files are allowed.');
      onChange(null, null);
      return;
    }
    
    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setUploadError(`File size exceeds ${maxSizeMB}MB limit.`);
      onChange(null, null);
      return;
    }
    
    setUploadError(null);
    
    // Generate local preview URL
    const localUrl = URL.createObjectURL(file);
    
    if (instantUpload && onUpload) {
      try {
        setIsUploading(true);
        // First update UI with local preview
        onChange(file, localUrl);
        
        // Then upload to server
        const uploadedUrl = await onUpload(file);
        
        // Update with remote URL
        onChange(file, uploadedUrl);
      } catch (err: any) {
        setUploadError(err.message || 'Failed to upload image');
        // Retain the file and local preview URL on error
        onChange(file, localUrl);
      } finally {
        setIsUploading(false);
      }
    } else {
      // Just update with local preview
      onChange(file, localUrl);
    }
  };

  const handleRemove = () => {
    onChange(null, null);
    if (inputRef.current) inputRef.current.value = '';
    setUploadError(null);
  };

  return (
    <div className={`photo-upload-container ${className}`}>
      {previewUrl ? (
        <div className="photo-preview">
          <img src={previewUrl} alt="Preview" />
          <button 
            type="button" 
            className="delete-photo-btn" 
            onClick={handleRemove} 
            disabled={disabled || isUploading}
            aria-label="Remove image"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          {isUploading && (
            <div className="upload-overlay">
              <FontAwesomeIcon icon={faSpinner} spin className="uploading-spinner" />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      ) : (
        <div className="photo-upload-box">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled || isUploading}
            className="phu-hidden-input"
            required={required}
            id="photo-upload-input"
            aria-label="Upload photo"
            title="Choose a photo to upload"
          />
          <button 
            type="button" 
            className="upload-btn" 
            onClick={() => inputRef.current?.click()} 
            disabled={disabled || isUploading}
          >
            {isUploading ? (
              <FontAwesomeIcon icon={faSpinner} spin className="upload-icon" />
            ) : (
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
            )}
            <span>{isUploading ? 'Uploading...' : label}</span>
          </button>
          <span>{value?.name || 'No file chosen'}</span>
        </div>
      )}
      {(error || uploadError) && (
        <div className="error-text">{error || uploadError}</div>
      )}
      {helperText && !error && !uploadError && (
        <div className="helper-text">{helperText}</div>
      )}
    </div>
  );
};

export default PhotoUpload;
